<template>
  <div class="card">
    <div class="row mb-2">
      <div class="col-6 align-self-center">
        <h2 class="subtitle mb-0">
          Product Access
          <el-popover placement="top" trigger="hover" width="300">
            <p class="form-control-label mb-2">
              <strong>What is Product Access?</strong>
            </p>
            <p class="text-justify f1 lh-f1 mb-0">
              Defines which products this reseller can see, check availability
              for and book through its API key. Product webhooks are only sent
              for the products it can access.
            </p>
            <div class="badge badge-primary" slot="reference">?</div>
          </el-popover>
        </h2>
      </div>
      <div
        class="col-6 d-flex justify-content-end align-items-center"
        v-if="canUserUpdate"
      >
        <base-button
          type="primary"
          class="min-button mb-0"
          :disabled="saving || !hasChanges"
          @click="saveProductAccess()"
        >
          <b-spinner v-if="saving" small class="mr-1" />
          <i v-else class="flaticon-floppy-disk"></i>
          {{ saving ? "Saving..." : "Save" }}
        </base-button>
      </div>
    </div>

    <b-form-group class="mb-2">
      <b-form-radio-group
        v-model="mode"
        :disabled="!canUserUpdate"
        id="radio-product-access"
        name="radio-product-access"
        buttons
        button-variant="outline-neutral"
        size="md"
        class="pc-0"
      >
        <b-form-radio :value="PRODUCT_ACCESS_MODES.ALL">All products</b-form-radio>
        <b-form-radio :value="PRODUCT_ACCESS_MODES.SELECTED">
          Selected products
        </b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <template v-if="mode === PRODUCT_ACCESS_MODES.SELECTED">
      <div class="row mb-2`">
        <div class="col-6 align-self-center">
          <div class="data-block mb-0">
            <span class="title-block">
              Selected products ({{ selectedProducts.length }})
            </span>
          </div>
        </div>
        <div class="col-6 alinmov2 align-self-center" v-if="canUserUpdate">
          <base-button
            type="link"
            class="min-button"
            @click="showPicker = true"
          >
            <i class="flaticon-plus"></i> Add products
          </base-button>
        </div>
      </div>

      <base-alert
        v-if="selectedProducts.length === 0"
        type="warning"
        class="info-min"
      >
        <i class="flaticon-warning mr-1"></i> There are no products selected
        here
      </base-alert>

      <b-table
        v-else
        :items="selectedProducts"
        :fields="fields"
        hover
        responsive
        size="sm"
      >
        <template #cell(name)="{ item }">
          <span v-if="item.unavailable" class="text-muted">
            Unavailable product
          </span>
          <span v-else>{{ item.name }}</span>
        </template>

        <template #cell(destination)="{ item }">
          {{ getDestinationName(item) }}
        </template>

        <template #cell(actions)="{ item }">
          <el-tooltip content="Remove" placement="bottom" v-if="canUserUpdate">
            <span
              class="icon-action text-danger"
              @click="removeProduct(item.uuid)"
            >
              <i class="flaticon-delete"></i>
            </span>
          </el-tooltip>
        </template>
      </b-table>

      <ResellerProductPicker
        :show.sync="showPicker"
        :addedUuids="selectedUuids"
        @add="addProducts"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { ProductsAPI, ResellersAPI } from "../../../core/api";
import { isUserAuthorized } from "../../../helpers/commons.helpers";
import { PRODUCT_ACCESS_MODES } from "../constants/product-access.constants";
import ResellerProductPicker from "./ResellerProductPicker.vue";

export default {
  name: "ResellerProductAccess",
  components: {
    ResellerProductPicker
  },
  props: {
    resellerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      PRODUCT_ACCESS_MODES,
      mode: PRODUCT_ACCESS_MODES.ALL,
      selectedProducts: [],
      savedAccess: { mode: PRODUCT_ACCESS_MODES.ALL, productIds: [] },
      saving: false,
      showPicker: false,
      fields: [
        { key: "name", label: "Product" },
        { key: "destination", label: "Destination" },
        { key: "actions", label: "", thStyle: { width: "40px" } }
      ]
    };
  },
  async created() {
    await this.loadProductAccess();
  },
  methods: {
    async loadProductAccess() {
      try {
        let { data } = await ResellersAPI.getResellerById(
          this.resellerId,
          this.sesion.token
        );
        const productAccess =
          data && data.productAccess
            ? data.productAccess
            : { mode: PRODUCT_ACCESS_MODES.ALL, productIds: [] };
        this.mode = productAccess.mode;
        this.savedAccess = {
          mode: productAccess.mode,
          productIds: [...(productAccess.productIds || [])]
        };
        this.selectedProducts = await this.getProductsByUuids(
          this.savedAccess.productIds
        );
      } catch (error) {
        this.$notify({
          type: "danger",
          title: "Error!",
          message: "The product access could not be loaded. Please try again",
          icon: "flaticon-remove"
        });
      }
    },
    async getProductsByUuids(uuids = []) {
      if (!uuids.length) return [];
      const query = uuids
        .map(uuid => `uuids=${encodeURIComponent(uuid)}`)
        .join("&");
      let { data } = await ProductsAPI.getProducts(
        uuids.length,
        1,
        query,
        this.sesion.token
      );
      const products = data && data.data ? data.data.docs : [];
      return uuids.map(
        uuid =>
          products.find(product => product.uuid === uuid) || {
            uuid,
            unavailable: true
          }
      );
    },
    addProducts(products = []) {
      const productsByUuid = new Map(
        this.selectedProducts.map(product => [product.uuid, product])
      );
      products.forEach(product => {
        if (!productsByUuid.has(product.uuid)) {
          productsByUuid.set(product.uuid, product);
        }
      });
      this.selectedProducts = [...productsByUuid.values()];
    },
    removeProduct(uuid) {
      this.selectedProducts = this.selectedProducts.filter(
        product => product.uuid !== uuid
      );
    },
    async saveProductAccess() {
      if (
        this.mode === PRODUCT_ACCESS_MODES.SELECTED &&
        this.selectedProducts.length === 0
      ) {
        this.$notify({
          type: "warning",
          title: "Important!",
          message: "Add at least one product or select All products",
          icon: "flaticon-warning-1"
        });
        return;
      }

      let { value } = await Swal.fire({
        title: "Update Product Access",
        html:
          "The change applies immediately to the reseller's active API key and product webhooks. Do you want to continue?",
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
        buttonsStyling: false,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        cancelButtonText: "No",
        confirmButtonText: "Yes"
      });
      if (value) {
        await this.patchProductAccess(this.buildProductAccess());
      }
    },
    async patchProductAccess(productAccess) {
      this.saving = true;
      try {
        let { data } = await ResellersAPI.patchResellerById(
          this.resellerId,
          { productAccess },
          this.sesion.token
        );
        if (data) {
          this.savedAccess = {
            mode: productAccess.mode,
            productIds: [...productAccess.productIds]
          };
          this.$notify({
            type: "success",
            title: "Success!",
            message: "The product access has been saved",
            icon: "flaticon-check-3"
          });
        } else {
          this.notifySaveError();
        }
      } catch (error) {
        this.notifySaveError(error);
      } finally {
        this.saving = false;
      }
    },
    notifySaveError(error = null) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
          ? error.response.data.errorMessage
          : "The product access could not be saved. Please try again";
      this.$notify({
        type: "danger",
        title: "Error!",
        message,
        icon: "flaticon-remove"
      });
    },
    buildProductAccess() {
      if (this.mode === PRODUCT_ACCESS_MODES.ALL) {
        return { mode: PRODUCT_ACCESS_MODES.ALL, productIds: [] };
      }
      return {
        mode: PRODUCT_ACCESS_MODES.SELECTED,
        productIds: this.selectedUuids
      };
    },
    getDestinationName(product) {
      return product.destination && product.destination.city
        ? product.destination.city
        : "-";
    }
  },
  computed: {
    ...mapGetters(["sesion", "userRole"]),
    canUserUpdate() {
      return isUserAuthorized("RESELLERS", this.userRole, "UPDATE");
    },
    selectedUuids() {
      return this.selectedProducts.map(product => product.uuid);
    },
    hasChanges() {
      const current = this.buildProductAccess();
      return (
        current.mode !== this.savedAccess.mode ||
        current.productIds.join(",") !== this.savedAccess.productIds.join(",")
      );
    }
  }
};
</script>
